import sortBy from "lodash/sortBy";
import isDesktop from 'lib/isDesktop';
import { getOffset, findActiveAnchor } from 'lib/unitPageScroll';

/*

This functionality is highly coupled with the UI,
of which the markup comes from the content system.
Trailhead-web has no control over the content,
which is why we have to use some DOM manipulation.

This could definitely be improved by updating selectors
to be more robust and some further decoupling by passing
DOM as args

*/

// its ugly if anchors go to the exact
// top of element, so we use an offset (global nav height + 1rem)
const SCROLL_OFFSET = 212;

// Return whichever context nav is rendered on the page.
// New server-rendered nav uses id="context-nav"; legacy XSF web component uses id="contextnav".
function getContextNavElement() {
  return document.getElementById("context-nav") || document.getElementById("contextnav");
}

// Live offset for sticky elements that anchor below the context nav.
// New nav: live bottom edge of the context nav (tracks the global nav sliding
// in/out as the user scrolls).
// Legacy nav: preserve previous behavior — use the context nav's own height —
// so flag-OFF rendering is unchanged.
function getContextNavOffset() {
  const newNav = document.getElementById("context-nav");
  if (newNav) {
    return Math.max(0, newNav.getBoundingClientRect().bottom);
  }
  const legacyNav = document.getElementById("contextnav");
  if (legacyNav) {
    return legacyNav.offsetHeight;
  }
  const globalNav = document.getElementById("global-nav-container");
  return globalNav ? globalNav.offsetHeight : 0;
}

export function smoothTopicNav() {
  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

  const navLinks = document.querySelectorAll(".topic-list-nav a");
  navLinks.forEach(link => {
    link.addEventListener("click", (e) => {
      if (!isIE11) {
        e.preventDefault();
      }

      const targetLink = e.target.closest("a");
      const targetElement = document.querySelector(targetLink.getAttribute("href"));
      if (targetElement) {
        const pos = getOffset(targetElement).top - SCROLL_OFFSET;

        window.scroll({
          top: pos,
          left: 0,
          behavior: "smooth"
        });
      }
    });
  });
}

export function stickyBreadcrumb() {
  const breadcrumbWrapper = document.querySelector(".th-sticky-breadcrumb");
  if (!breadcrumbWrapper) {
    return;
  }

  const breadcrumb = breadcrumbWrapper.children[0];
  if (!breadcrumb) return;

  const shim = document.createElement('div');
  shim.className = 'th-sticky-breadcrumb__shim';
  shim.style.width = "100%";

  // Capture intrinsic height before going fixed so the shim reserves space.
  const intrinsicHeight = breadcrumb.offsetHeight;

  breadcrumb.style.position = "fixed";
  breadcrumb.style.width = "100%";
  breadcrumb.style.top = `${getContextNavOffset()}px`;

  shim.style.height = `${intrinsicHeight}px`;
  breadcrumbWrapper.insertAdjacentElement('afterend', shim);

  // Track the current top so we only assign when it changes — avoids layout
  // thrash on every scroll event.
  let currentTop = -1;
  const setPosition = () => {
    const top = getContextNavOffset();
    if (top !== currentTop) {
      breadcrumb.style.top = `${top}px`;
      currentTop = top;
    }
  };

  return {
    onScroll: setPosition
  };
}

export function stickyTopicNav() {
  const sidebar = document.getElementById("unit-sidebar");
  if (!sidebar) return null;

  const setSidebarPosition = () => {
    const contextNav = getContextNavElement();
    if (contextNav) {
      sidebar.style.position = "sticky";
      sidebar.style.top = `${155 + contextNav.offsetTop}px`;
    }
  };

  return {
    onScroll: setSidebarPosition
  };
}

export function scrollSpyTopicNav() {
  const navLinks = document.querySelectorAll(".topic-list-nav a");
  if (navLinks.length === 0) {
    return;
  }

  // clean topic nav, because sometimes there are empty items
  // ex: /modules/apex_integration_services/units/apex_integration_callouts
  Array.from(navLinks).forEach(link => {
    if (link.innerHTML.trim() === "") {
      link.remove();
    }
  });

  // rebuild list after removing empty items
  const activeNavLinks = Array.from(document.querySelectorAll(".topic-list-nav a"));

  const createAnchorMap = () => {
    const anchorHrefs = activeNavLinks.map(a => a.getAttribute("href"));
    const bodyAnchors = anchorHrefs
      .map(href => document.querySelector(href))
      .filter(el => el !== null);

    const anchorMap = bodyAnchors.map(a => ({
      selector: `#${a.getAttribute("id")}`,
      offsetTop: getOffset(a).top - SCROLL_OFFSET - 5
    }));

    return sortBy(anchorMap, "offsetTop");
  };

  const findActiveAnchorForPosition = (position) => {
    // recreate map b/c window can resize and
    // positions shift as images load etc
    const aMap = createAnchorMap();
    return findActiveAnchor(aMap, position);
  };

  const setActiveAnchor = (position) => {
    const activeItems = document.querySelectorAll(".topic-list-nav li.active");
    activeItems.forEach(item => {
      item.classList.remove("active");
      const link = item.querySelector("a");
      if (link) {
        link.removeAttribute("aria-current");
      }
    });

    const activeAnchor = findActiveAnchorForPosition(position);
    if (!activeAnchor) return;

    const anchorHref = activeAnchor.selector;

    const anchor = activeNavLinks.find(a => a.getAttribute("href") === anchorHref);

    if (anchor) {
      const listItem = anchor.closest("li");
      if (listItem) {
        listItem.classList.add("active");
      }
      anchor.setAttribute('aria-current', 'true');
    }
  };

  return {
    onScroll: setActiveAnchor
  };
}

export function scrollSidePanel() {
  const unitContentContainer = document.querySelector(".unit-content-container");
  const unitHeading = document.querySelector(".th-content-unit-heading");
  const contextNav = getContextNavElement();
  const padding = 10;
  // Site banners can stack, so account for every alert, not just the first.
  const alertHeight = Array.from(document.querySelectorAll("th-alert"))
    .reduce((total, alert) => total + alert.offsetHeight, 0);
  if (!unitContentContainer || !unitHeading) {
    return;
  }
  const unitHeadingHeight = unitHeading.offsetHeight;

  const setSidePanelContentHeight = () => {
    if (unitContentContainer && contextNav) {
      const contextNavOffset = contextNav.offsetTop + contextNav.offsetHeight;
      unitContentContainer.style.height = `calc(100vh - ${unitHeadingHeight + padding + alertHeight}px - ${contextNavOffset}px)`;
    }
  };

  return {
    onScroll: setSidePanelContentHeight
  };
}

export function unitPageOnScroll() {
  const stickyNav = stickyTopicNav();
  const scrollSpy = scrollSpyTopicNav();
  const sticky = stickyBreadcrumb();
  const sidePanel = scrollSidePanel();

  const onScroll = () => {
    const currentPosition = window.pageYOffset || document.documentElement.scrollTop;

    try {
      if (sticky) {
        sticky.onScroll(currentPosition);
      }
      if (stickyNav) {
        stickyNav.onScroll(currentPosition);
      }
      if (scrollSpy) {
        scrollSpy.onScroll(currentPosition);
      }
      if (sidePanel) {
        sidePanel.onScroll();
      }
    } catch (e) {
      console.warn("topic nav error", e);
    }
  };

  return {
    onScroll
  };
}

export default function unitPage() {
  const init = () => {
    try {
      smoothTopicNav();
      // unitPageOnScroll().onScroll();
      const sidePanel = scrollSidePanel();

      if (sidePanel) {
        sidePanel.onScroll();
      }
    } catch (e) {
      console.warn("could not initialize topic nav", e);
    }
  };

  return {
    init,
    onScroll: unitPageOnScroll().onScroll
  };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnitPage);
} else {
  initUnitPage();
}

function initUnitPage() {
  const page = unitPage();
  page.init();

  window.addEventListener("scroll", page.onScroll);
  window.addEventListener("scrollend", page.onScroll);

  window.addEventListener("resize", page.onScroll);

  if (isDesktop()) {
    const unitContent = document.querySelector(".th-unit-content");
    if (unitContent) {
      unitContent.classList.add('desktop');
    }
  }
}
